import CloseIcon from "@mui/icons-material/Close";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import IsTestingAppWrapper from "../../wrapper/IsTestingAppWrapper/IsTestingAppWrapper";
import { APP_STAGE_BAR_HEIGHT } from "../../../helpers/constants";
import { isAppTesting } from "../../../helpers/methods";

function PaperComponent(props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

const defaultProps = {
    open: false,
    titleAlign: "left",
    titleFontSize: "24px",
    titleSx: {},
    contentAlign: "center",
    dividers: true,
    showTitle: true,
    maxWidth: "lg",
    alwaysFullScreen: false,
    showDialogActions: true,
    showCustomFooter: false,
    disableOnCloseButton: false,
    showTopActionButton: false,
    showBottomActionButton: false,
    dialogSx: {},
    contentSx: {},
    showTopCloseButton: true,
    showTopFullScreenButton: true,
    showBottomCloseButton: false,
    hide: false,
    disableDraggable: false,
};

export default function BasicDialog(providedProps) {
    const props = { ...defaultProps, ...providedProps };
    const { t } = useTranslation();

    const getLabelCloseButton = () => {
        if (props.closeButtonLabel) {
            return props.closeButtonLabel;
        }
        return t("close");
    };

    const [fullScreen, setFullScreen] = useState(props.alwaysFullScreen);
    const showTestBar = fullScreen && isAppTesting();

    const toggleFullScreen = () => {
        setFullScreen((prev) => !prev);
        if (props.onToggleFullScreen) {
            props.onToggleFullScreen(!fullScreen);
        }
    };

    const handleScrollDialogContent = (e) => {
        if (props.onScrollDialogContent) {
            props.onScrollDialogContent(e);
        }
    };

    return (
        <Dialog
            fullWidth
            open={props.open}
            maxWidth={props.maxWidth}
            onClose={props.onClose}
            sx={{
                opacity: props.hide ? 0 : 1,
                display: props.open ? undefined : "none",
                backgroundColor: props.backgroundColor,
                "& .MuiDialog-paper": {
                    minHeight: props.minHeight,
                    maxHeight: props.maxHeight === false ? undefined : props.maxHeight,
                    minWidth: props.minWidth,
                    paddingTop: showTestBar ? APP_STAGE_BAR_HEIGHT : undefined,
                },
                ...props.dialogSx,
            }}
            PaperComponent={
                props.alwaysFullScreen || props.disableDraggable ? undefined : PaperComponent
            }
            fullScreen={fullScreen}
            slotProps={{
                backdrop: {
                    style: { backgroundColor: props.backdropColor },
                },
            }}
        >
            {fullScreen && <IsTestingAppWrapper />}
            {props.showTitle && props.title && (
                <DialogTitle
                    align={props.titleAlign}
                    id="draggable-dialog-title"
                    style={{
                        cursor: "move",
                        color: "var(--primary)",
                        fontSize: props.titleFontSize,
                    }}
                    sx={{
                        "& .MuiDialogTitle-root": {
                            color: "var(--primary)",
                        },
                        ...props.titleSx,
                    }}
                >
                    {props.title.charAt(0).toUpperCase() + props.title.slice(1)}
                    {props.showTopActionButton && (
                        <Grid item xs={12} style={{ padding: "10px" }} alignItems="flex-start">
                            {props.topActionButton}
                        </Grid>
                    )}
                </DialogTitle>
            )}
            {props.showTopCloseButton && (
                <IconButton
                    aria-label="close"
                    onClick={props.onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: showTestBar ? `calc(${APP_STAGE_BAR_HEIGHT} + 16px)` : 16,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            )}
            {props.showTopFullScreenButton && !props.alwaysFullScreen && (
                <IconButton
                    aria-label="full_screen"
                    onClick={toggleFullScreen}
                    sx={{
                        position: "absolute",
                        right: 48,
                        top: showTestBar ? `calc(${APP_STAGE_BAR_HEIGHT} + 16px)` : 16,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    {fullScreen ? <FullscreenExitOutlinedIcon /> : <FullscreenOutlinedIcon />}
                </IconButton>
            )}
            {props.headerComponent}
            <DialogContent
                dividers={props.dividers}
                sx={{
                    textAlign: props.contentAlign,
                    ...props.contentSx,
                }}
                onScroll={handleScrollDialogContent}
            >
                {props.children}
            </DialogContent>
            {props.showBottomActionButton && (
                <Grid
                    container
                    style={props.bottomActionStyle}
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                >
                    {props.bottomActionButton}
                </Grid>
            )}
            {!props.showCustomFooter && props.showDialogActions && (
                <DialogActions style={{ cursor: "move" }} id="draggable-dialog-title">
                    {props.showBottomCloseButton && (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={props.onClose}
                            sx={props.closeButtonSx}
                            disabled={props.disableOnCloseButton}
                        >
                            {getLabelCloseButton()}
                        </Button>
                    )}
                </DialogActions>
            )}
            {props.showCustomFooter && (
                <DialogActions
                    sx={{ alignItems: "flex-end" }}
                    id="draggable-dialog-title"
                    style={{ cursor: "move" }}
                >
                    {props.customFooter}
                    <Button
                        variant="contained"
                        size="small"
                        onClick={props.onClose}
                        sx={{ verticalAlign: "bottom" }}
                    >
                        {getLabelCloseButton()}
                    </Button>
                </DialogActions>
            )}
            {props.renderFooter?.()}
        </Dialog>
    );
}
